import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Glyph } from './glyphs';
import { cn } from '../../lib/cn';
import { Wordmark } from '../../brand';
import { Illustration } from '../../illustrations';
import { Sheet, SheetBody } from '../mobile';
import '../../patterns/mobile-search.css';

/**
 * The search entry point of the mobile header.
 *
 * WHY A COLLAPSED TRIGGER AND A SHEET, NOT A FIELD IN THE BAR
 * ----------------------------------------------------------
 * The field used to live in the bar itself. It worked, but it took the CENTRE
 * slot — the only slot wide enough for it — and the centre slot is where the
 * wordmark goes. So the brand disappeared from every surface that could search,
 * which is the three surfaces a buyer spends their time on. A logo that is
 * absent exactly where people look most is not a trade worth making, and it is
 * the reason this was rebuilt.
 *
 * Collapsed, the entry point costs a 44px icon in the end slot next to the
 * cart, the wordmark keeps the left edge, and the search surface gets a whole
 * sheet instead of a strip.
 *
 * THE OBJECTION THIS HAS TO ANSWER, because the previous version raised it:
 * a bottom sheet is anchored to the bottom of the screen, which is where the
 * keyboard opens. That is true and it is why the sheet is TALL rather than
 * fitted to its content (`.dsa-msearch-sheet`, 89% of the shell): the field
 * sits at the TOP of a near-full-height sheet, so the keyboard rises into the
 * empty space under the results rather than over the thing you are typing in.
 * A short sheet sized to two rows of history would have put the field on the
 * keyboard's top edge, which is the failure the old comment predicted.
 *
 * It does NOT own a search. Everything here is `useGlobalSearch()`'s, handed
 * down by the shell: the same value, the same typeahead sections, the same
 * submit that goes to `/search?q=`. The desktop box is the same hook, and the
 * rows below are the same rows in the same order — the sheet is the dropdown,
 * relocated.
 *
 * A `<form>`, not a `<label>`, though `.ds-search` is documented as
 * label-wrapped: the clear button is a real button, and a button inside a
 * label is activated twice by one tap. `ConsoleShell`'s drawer search made the
 * same swap for the same reason. The input carries its own `aria-label`.
 */
function SearchTrigger({ onOpen, label = 'Search' }) {
  return (
    <button
      type="button"
      className="ds-iconbtn ds-iconbtn--bare dsa-msearch__trigger"
      onClick={onOpen}
      aria-label={label}
      aria-haspopup="dialog"
    >
      <Glyph name="search" />
    </button>
  );
}

function MobileSearchSheet({ search, open, onClose }) {
  const {
    value = '',
    onChange,
    onSubmit,
    sections = [],
    emptyState,
    onSelectSuggestion,
    onRemoveSuggestion,
    placeholder = 'Search cards, sets, sealed product',
    label = 'Search',
  } = search;

  const inputRef = useRef(null);

  const groups = sections.filter((section) => section.items && section.items.length > 0);

  const handleSubmit = (event) => {
    event.preventDefault();
    onClose();
    if (onSubmit) onSubmit(inputRef.current ? inputRef.current.value : value);
  };

  const handleSelect = (item) => {
    onClose();
    if (onSelectSuggestion) onSelectSuggestion(item);
  };

  /* Removing does NOT close the sheet — you delete a remembered search to look
     at the shorter list, and closing on the first X would make clearing three
     of them three round trips. */
  const handleRemove = (item) => {
    if (onRemoveSuggestion) onRemoveSuggestion(item);
  };

  return (
    /* `initialFocusRef` on the input rather than letting the Sheet take the
       first focusable it finds. Opening a search and having to tap again to
       type is the whole cost of collapsing it, so the keyboard comes up with
       the sheet. `Sheet` already keeps itself mounted through its exit
       animation, so there is no `open &&` guard here — that is what left three
       other sheets in this app with an entry animation and no exit. */
    <Sheet
      open={open}
      onClose={onClose}
      className="dsa-msearch-sheet"
      aria-label={label}
      initialFocusRef={inputRef}
    >
      <div className="dsa-msearch-sheet__head">
        <form className="ds-search" role="search" onSubmit={handleSubmit}>
          <Glyph name="search" />
          <input
            ref={inputRef}
            className="ds-search__input"
            type="search"
            aria-label={label}
            placeholder={placeholder}
            value={value}
            onChange={onChange ? (event) => onChange(event.target.value) : undefined}
            readOnly={!onChange}
          />
          {value && onChange ? (
            <button
              type="button"
              className="dsa-search-clear"
              aria-label="Clear search"
              onClick={() => {
                onChange('');
                /* Someone who clears a query is about to type another one. */
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              <Glyph name="x" />
            </button>
          ) : null}
        </form>
      </div>

      <SheetBody className="dsa-msearch-sheet__body">
        {emptyState ? (
          <div className="dsa-msearch__prompt">
            {/* The sheet has the height the strip did not, so the prompt can
                carry its artwork the way the desktop panel does. `sm`, not the
                default: the recent searches under it still have to be reachable
                without scrolling. */}
            <Illustration name="search-not-started" size="sm" />
            <p className="dsa-msearch__prompt-title">{emptyState.title}</p>
            {emptyState.body ? (
              <p className="dsa-msearch__prompt-body">{emptyState.body}</p>
            ) : null}
          </div>
        ) : null}
        {groups.map((group) => (
          <React.Fragment key={group.id}>
            {group.title ? (
              <p className="dsa-msearch__head" aria-hidden="true">
                {group.title}
              </p>
            ) : null}
            <ul className="ds-menu" role="listbox" aria-label={group.title || label}>
              {group.items.map((item) => (
                <li key={`${group.id}-${item.id}`} role="option" aria-selected="false">
                  {/* Row and remove control are SIBLINGS: a <button> inside a
                      <button> is invalid and the browser drops one of them. */}
                  <span
                    className={cn(
                      'dsa-msearch__row',
                      item.removable && onRemoveSuggestion && 'dsa-msearch__row--removable',
                    )}
                  >
                    {/* `onClick`, not `onMouseDown`. Inside a sheet there is no
                        outside-press race to lose to — the scrim owns dismissal
                        — and click is the only activation a screen reader or a
                        paired keyboard dispatches, so a pointer-only handler
                        would leave these rows unreachable to both. */}
                    <button
                      type="button"
                      className="ds-menu__row"
                      onClick={() => handleSelect(item)}
                    >
                      {item.thumb ? (
                        <img src={item.thumb} alt="" className="dsa-msearch__thumb" />
                      ) : (
                        /* The mobile track draws its own 24-grid glyphs and
                           never Phosphor, so `item.icon` — a Phosphor component
                           the desktop panel renders — is deliberately ignored
                           here. Every iconless row in this panel is a query (a
                           remembered search, "see all results for"), and the
                           magnifier says so. */
                        <span className="ds-menu__icon">
                          <Glyph name="search" />
                        </span>
                      )}
                      <span className="ds-menu__label">{item.label}</span>
                      {item.sub ? <span className="dsa-msearch__sub">{item.sub}</span> : null}
                    </button>
                    {item.removable && onRemoveSuggestion ? (
                      /* ALWAYS VISIBLE on this track. The desktop control
                         reveals on hover; a touch screen has no hover, and a
                         control that only appears once you are already pressing
                         is a control nobody finds. It is 44px, like everything
                         else a thumb has to hit here. */
                      <button
                        type="button"
                        className="dsa-msearch__remove"
                        aria-label={`Remove ${item.label} from recent searches`}
                        onClick={() => handleRemove(item)}
                      >
                        <Glyph name="x" />
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </React.Fragment>
        ))}
      </SheetBody>
    </Sheet>
  );
}

/**
 * The mobile header, `.ds-topbar`.
 *
 * A three slot 44 / 1fr / 44 grid. The left slot holds either the floating
 * circular back button or the drawer trigger, the centre holds the page title
 * or the wordmark, the right holds at most two bare icon controls.
 *
 * `back` renders `.ds-backbtn`, which IS the floating circular button: raised
 * card surface, hairline, soft shadow. It is the mobile counterpart of desktop
 * breadcrumbs, and a page takes one or the other, never both.
 *
 * `search` no longer touches the centre slot. It adds a 44px magnifier to the
 * END slot, beside the cart, and opens a bottom sheet — so the wordmark or the
 * title keeps the middle on every surface, including the browse surfaces that
 * used to give it up. See `SearchTrigger` / `MobileSearchSheet` above for why
 * that changed and how the sheet answers the keyboard problem.
 *
 * @param {{
 *   title?: string,
 *   wordmark?: boolean,
 *   back?: { to?: string, state?: any, onClick?: () => void, label?: string },
 *   menu?: { onClick: () => void, label?: string, controls?: string, expanded?: boolean },
 *   search?: object,
 *   end?: React.ReactNode,
 *   className?: string
 * }} props
 */
export function MobileTopBar({ title, wordmark = false, back, menu, search, end, className }) {
  /* THE LOGO DOCKS LEFT. The bar is a 44 / 1fr / 44 grid whose first track
     exists for the back button or the drawer trigger. On a wordmark bar there
     is neither, so the placeholder span held that track open and the lockup
     started 56px in (44 + the 12px gap) and read as floating in the middle of
     the row rather than sitting at the header's own gutter. With no lead
     element the grid loses the track and the logo takes the left edge, in line
     with everything below it. */
  const brandLead = wordmark && !back && !menu;

  /* The sheet's own open state lives here rather than in the shell: nothing
     outside this bar opens the mobile search, and threading it up through
     `MarketShell` would put a piece of one component's UI state on every page
     that renders a header. */
  const [searchOpen, setSearchOpen] = useState(false);

  const lead = () => {
    if (brandLead) return null;
    if (back) {
      const label = back.label || 'Go back';
      if (back.to) {
        return (
          /* `state` rides with the `to` on both tracks. See `BackLink` in
             MarketShell.jsx for why a way back needs to carry one. */
          <Link to={back.to} state={back.state} className="ds-backbtn" aria-label={label}>
            <Glyph name="arrowLeft" />
          </Link>
        );
      }
      return (
        <button type="button" className="ds-backbtn" onClick={back.onClick} aria-label={label}>
          <Glyph name="arrowLeft" />
        </button>
      );
    }

    if (menu) {
      return (
        <button
          type="button"
          className="ds-iconbtn ds-iconbtn--bare"
          onClick={menu.onClick}
          aria-label={menu.label || 'Open navigation'}
          aria-controls={menu.controls}
          aria-expanded={Boolean(menu.expanded)}
        >
          <Glyph name="list" />
        </button>
      );
    }

    /* The empty span is a GRID PLACEHOLDER: without it the title falls into
       the 44px lead track. */
    return <span />;
  };

  const centre = () => {
    /* `md`, not `sm`: 24px against 20px, the 15% step Junaid asked for, and it
       is the size the desktop nav already uses — one lockup size across the
       app rather than a smaller one on the smaller screen. */
    if (wordmark) return <Wordmark className="ds-topbar__wordmark" />;
    return <span className="ds-topbar__title">{title}</span>;
  };

  return (
    <header
      className={cn('ds-topbar', 'ds-appshell__topbar', brandLead && 'ds-topbar--brand', className)}
    >
      {lead()}
      {centre()}
      {/* Search LEADS the end slot, so the cart keeps the far corner it has
          held on both tracks since before the conversion. Two 44px controls fit
          the `auto` track the shell opens for exactly this. */}
      <span className="ds-appshell__topbar-end">
        {search ? <SearchTrigger onOpen={() => setSearchOpen(true)} label={search.label} /> : null}
        {end}
      </span>
      {search ? (
        <MobileSearchSheet
          search={search}
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </header>
  );
}

export default MobileTopBar;
